/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import {useTheme} from '../styles';
import {FluidOverlay, TOverlayProps} from './Overlay';
import * as S from './style';
import {TCloseWhen, TCloseWhenHandler, THead, TOverlay, TTrigger} from './types';
import {useCloseHandler} from './useCloseHandler';
import {makeValidElement} from './utils';

const useMouseOver = (callback: (state: boolean) => void, hoverDelay?: number) => {
  const theme = useTheme();

  const leaveTimeout = hoverDelay ?? theme.animation.duration / 2;

  const handleMouseLeave = React.useMemo(
    () => debounce(() => callback(false), leaveTimeout),
    [callback, leaveTimeout],
  );

  const handleMouseEnter = React.useCallback(() => {
    handleMouseLeave.cancel();
    callback(true);
  }, [callback, handleMouseLeave]);

  return [handleMouseEnter, handleMouseLeave];
};

export type TProps = TOverlayProps & {
  className?: string;
  head?: THead;
  headComponent?: TAnyComponent;
  overlay?: TOverlay;
  trigger?: TTrigger;
  closeWhen?: TCloseWhen | TCloseWhenHandler;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  disabled?: boolean;
  readOnly?: boolean;
  scrollTo?: number;
  onOpen?: TEmptyFunction;
  onClose?: TEmptyFunction;
  hoverDelay?: number;
  disableHoverOverlay?: boolean;
  overlayClassName?: string;
};

export const Dropdown = React.memo<TProps>(
  ({
    className,
    head,
    headComponent: Head = S.Head,
    overlay,
    overlayComponent = S.Overlay,
    placementX,
    placementY,
    offsetY = 10,
    offsetX,
    trigger = 'click',
    closeWhen = 'clickAny',
    onFocus = noop,
    onBlur = noop,
    disabled,
    readOnly,
    overlayColor,
    hasOverlayArrow,
    hasVerticalPaddings,
    scrollTo,
    onOpen = noop,
    onClose = noop,
    hoverDelay,
    disableHoverOverlay,
    overlayClassName,
  }) => {
    const {animation} = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);

    const delay = React.useMemo(() => {
      if (trigger === 'hover') {
        return hoverDelay ?? animation.duration / 2;
      }

      return animation.duration;
    }, [animation.duration, hoverDelay, trigger]);

    const toggleIsOpen = React.useCallback(() => setIsOpen((prevState) => !prevState), []);
    const handleClose = React.useCallback(() => {
      setIsOpen(false);

      if (closeWhen === 'clickOutsideDropdown') {
        onClose();
      }
    }, [closeWhen, onClose]);

    const [handleMouseEnter, handleMouseLeave] = useMouseOver(setIsOpen, delay);

    const [headRef, overlayRef, dropdownRef] = useCloseHandler(
      isOpen && trigger === 'click',
      closeWhen,
      handleClose,
    );

    const headHandlers = React.useMemo(() => {
      if (disabled || readOnly) {
        return {};
      }

      const handlers: Record<string, TAnyFunction> = {onFocus, onBlur};

      if (trigger === 'hover') {
        handlers.onMouseEnter = handleMouseEnter;
        handlers.onMouseLeave = handleMouseLeave;
      }

      if (trigger === 'click' && closeWhen === 'clickOutsideDropdown') {
        handlers.onClick = () => setIsOpen(true);

        return handlers;
      }

      if (trigger === 'click' && closeWhen !== 'clickOutsideDropdown') {
        handlers.onClick = toggleIsOpen;
      }

      return handlers;
    }, [
      closeWhen,
      disabled,
      readOnly,
      onFocus,
      onBlur,
      trigger,
      toggleIsOpen,
      handleMouseEnter,
      handleMouseLeave,
    ]);

    const overlayHandlers = React.useMemo(() => {
      const handlers: Record<string, TAnyFunction> = {};

      if (trigger === 'hover' && !disableHoverOverlay) {
        handlers.onMouseEnter = handleMouseEnter;
        handlers.onMouseLeave = handleMouseLeave;
      }

      return handlers;
    }, [trigger, disableHoverOverlay, handleMouseEnter, handleMouseLeave]);

    React.useEffect(() => {
      if (closeWhen === 'clickOutsideDropdown') {
        return;
      }

      if (isOpen) {
        onOpen();
      } else {
        onClose();
      }
    }, [isOpen, onClose, onOpen]);

    React.useEffect(() => {
      if (isOpen && scrollTo !== undefined && overlayRef.current) {
        overlayRef.current.scrollTop = scrollTo;
      }
    }, [isOpen, scrollTo, overlayRef]);

    return (
      <S.Dropdown className={className} ref={dropdownRef}>
        <Head {...headHandlers} ref={headRef} role="button" tabIndex={0} disabled={disabled}>
          {makeValidElement(head, {isOpen})}
        </Head>
        <FluidOverlay
          {...overlayHandlers}
          headRef={headRef}
          isOpen={isOpen}
          delay={delay}
          hasOverlayArrow={hasOverlayArrow}
          innerRef={overlayRef}
          overlayColor={overlayColor}
          hasVerticalPaddings={hasVerticalPaddings}
          placementY={placementY}
          placementX={placementX}
          offsetY={offsetY}
          offsetX={offsetX}
          overlayComponent={overlayComponent}
          className={overlayClassName}
        >
          {makeValidElement(overlay, {close: handleClose})}
        </FluidOverlay>
      </S.Dropdown>
    );
  },
);
/* eslint-enable react/jsx-props-no-spreading */
